import React from 'react';
import { Calendar, Info, Settings } from 'lucide-react';
import SettingsModal from './SettingsModal';
import StatusToast from './StatusToast';
import { formatSignedInAs } from '../utils/formatting';

// Extracted from App.js (App.js modularization, Sep 2026) — the
// step === 'create-calendar' screen, shown to a GA/SA-authority account
// that doesn't yet own or deputise any calendar.
//
// Bug found + fixed during this extraction (5 Sep): the Settings button
// here only ever called setShowSettings(true) — <SettingsModal> was never
// actually mounted in this early-return block, so the button did nothing.
// Same class of bug as the member-calendar permissions gap fixed 4 Sep
// (App.js's several step-based early returns each need their own copy of
// any modal they use). Not restricted to Profile/Preferences here, since
// this screen is for GA/SA-authority accounts, not plain members —
// matches the unrestricted pattern used on the main dashboard.
const CreateCalendarStep = ({
  statusMessage,
  onOpenUserGuide,
  onCancel,
  isAuthenticated,
  onSignOut,
  showSettings,
  setShowSettings,
  settingsInitialTab,
  setSettingsInitialTab,
  calendarName,
  setCalendarName,
  calendarDescription,
  setCalendarDescription,
  hostBoardEnabled,
  setHostBoardEnabled,
  onCreateCalendar,
  isCreatingCalendar,
  userEmail,
  apiBaseUrl,
  authToken,
  onStatusMessage,
  userTier,
  firstName,
  lastName,
  preferredName,
  isSA,
  raRegions,
  onSaveProfile,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Toast Notification - Centered (this screen has its own early
          return, separate from the main dashboard return below, so it
          never reached that toast markup — added here so create-calendar
          errors, e.g. authority/permission failures, are actually visible) */}
      <StatusToast statusMessage={statusMessage} />
      {/* HEADER */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Telematrix Calendar Hub</h1>
                <p className="text-xs text-gray-500">Create your calendar</p>
                {isAuthenticated && userEmail && (
                  <p className="text-xs text-gray-400">
                    Signed in as: {formatSignedInAs(preferredName, firstName, lastName, userEmail)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <button
                onClick={onOpenUserGuide}
                className="flex items-center gap-1 px-2 py-1.5 text-gray-600 hover:text-blue-600 text-xs font-medium transition whitespace-nowrap"
              >
                <Info className="w-3.5 h-3.5" />
                Guide
              </button>
              <button
                onClick={onCancel}
                className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition whitespace-nowrap"
              >
                Cancel
              </button>
              {isAuthenticated && (
                <button
                  onClick={onSignOut}
                  className="px-2 py-1.5 text-xs text-red-600 hover:text-red-700 font-medium border border-red-300 rounded-lg hover:bg-red-50 transition whitespace-nowrap"
                >
                  Sign Out
                </button>
              )}
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center gap-1 px-2 py-1.5 text-gray-600 hover:text-gray-900 text-xs whitespace-nowrap"
              >
                <Settings className="w-3.5 h-3.5" />
                Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CREATE CALENDAR FORM */}
      <div className="flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - 80px)' }}>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800">New Shared Calendar</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Calendar Name *</label>
              <input type="text" value={calendarName} onChange={(e) => setCalendarName(e.target.value)} placeholder="e.g., Book Club Events" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
              <textarea value={calendarDescription} onChange={(e) => setCalendarDescription(e.target.value)} placeholder="What's this calendar for?" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none h-20 resize-none" />
            </div>

            {/* (20 Sep 2026) Optional host schedule board, for groups that rotate
                hosting each month. Off by default; the GA can change it later. */}
            <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(hostBoardEnabled)}
                onChange={(e) => setHostBoardEnabled(e.target.checked)}
                className="mt-1"
              />
              <span>
                {/* (8 Oct 2026, decision 32) "Hosting schedule", as everywhere else. */}
                <span className="block text-sm font-semibold text-gray-700">Use a hosting schedule</span>
                <span className="block text-xs text-gray-500">
                  For groups that take turns hosting each month. You can change this later.
                </span>
              </span>
            </label>

            <button
              // (8 Oct 2026, decision 40) Not disabled for a missing name: pressing it says so.
              onClick={() => {
                if (!calendarName || !calendarName.trim()) {
                  onStatusMessage('Please give the calendar a name.', 'error');
                  return;
                }
                onCreateCalendar();
              }}
              disabled={isCreatingCalendar}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isCreatingCalendar ? 'Creating...' : 'Create Calendar'}
            </button>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-800">Members can subscribe to this calendar and get automatic updates.</p>
          </div>
        </div>
      </div>

      {showSettings && (
        <SettingsModal
          isOpen={showSettings}
          onClose={() => { setShowSettings(false); setSettingsInitialTab('profile'); }}
          userEmail={userEmail}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          onStatusMessage={onStatusMessage}
          userTier={userTier}
          initialTab={settingsInitialTab}
          currentCalendar={null}
          firstName={firstName}
          lastName={lastName}
          isSA={isSA}
          raRegions={raRegions}
          onSaveProfile={onSaveProfile}
          restrictToProfileTabs={false}
        />
      )}
    </div>
  );
};

export default CreateCalendarStep;
